import {all, call, fork, put, takeEvery} from 'bukku/shared/SagaService';
import {message} from 'antd';
import actions from './actions';
import AppActions from '../App/actions';
import {deleto, get, post, puto} from 'bukku/shared/ApiService';
import {t} from "@lingui/macro";
import PlanActions from 'bukku/redux/Plan/actions';
import { readSubscriptionHasDummyPacks, subscriptionDummyPacks } from './dummyData';
import { dummySubscriptionEng3265, useDummySubscriptionEng3265 } from '../../containers/AppPage/ControlPanel/Subscription/QuotaSubscription/DowngradeAlert/dummySubscription';
import dayjs from 'dayjs';
import { PreviewInvoiceType } from '../../components/Modal/SubscribeSwitchPlan/components/OrderSummaryV2/enums/PreviewInvoiceType';
import { getDummyPreviewInvoice } from './PreviewInvoiceRequest/getDummyPreviewInvoice';
import { postprocessInvoicePreview } from './PreviewInvoiceRequest/postprocessInvoicePreview';
import { debounce } from 'redux-saga/effects';

// READ
export function* readRequest() {
    yield takeEvery(actions.SUBSCRIPTION_READ_REQUEST, function* (payload, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, '/subscription', null, context);
            const data = yield response.data;
            let newSubscription = {
                ...data.subscription,
            };

            if (useDummySubscriptionEng3265)
                newSubscription = dummySubscriptionEng3265;

            if (readSubscriptionHasDummyPacks)
                newSubscription.addon_packs = subscriptionDummyPacks;

            // OK
            yield put({
                type: actions.SUBSCRIPTION_READ_SUCCESS,
                subscription: newSubscription,
            });

            let planInUse;
            if (dayjs().isSameOrBefore(dayjs(newSubscription.previous_plan_valid_until)))
                planInUse = newSubscription.previous_plan;
            else
                planInUse = newSubscription.current_plan;

            // set initial plan Billing Cycle
            if (planInUse?.billing_cycle) {
                yield put({
                    type: PlanActions.PLAN_SET_BILLING_CYCLE,
                    billingCycle: planInUse.billing_cycle,
                });
            }
        }
        catch (error) {
            yield put({
                type: actions.SUBSCRIPTION_READ_FAILURE,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}
export function* readSuccess() {
    yield takeEvery(actions.SUBSCRIPTION_READ_SUCCESS, () => {});
}
export function* readFailure() {
    yield takeEvery(actions.SUBSCRIPTION_READ_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Create
export function* createRequest() {
    yield takeEvery(actions.SUBSCRIPTION_CREATE_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(post, '/subscription', request, context);

            // OK
            yield put({
                type: actions.SUBSCRIPTION_CREATE_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.SUBSCRIPTION_CREATE_FAILURE,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}
export function* createSuccess() {
    yield takeEvery(actions.SUBSCRIPTION_CREATE_SUCCESS, function* () {
        yield message.success(t`Your subscription has been activated.`);
    });
}
export function* createFailure() {
    yield takeEvery(actions.SUBSCRIPTION_CREATE_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Switch
export function* switchRequest() {
    yield takeEvery(actions.SUBSCRIPTION_SWITCH_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(puto, '/subscription', request, context);

            // OK
            yield put({
                type: actions.SUBSCRIPTION_SWITCH_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.SUBSCRIPTION_SWITCH_FAILURE,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}
export function* switchSuccess() {
    yield takeEvery(actions.SUBSCRIPTION_SWITCH_SUCCESS, function* () {
        yield message.success(t`Your subscription plan is updated.`);
    });
}
export function* switchFailure() {
    yield takeEvery(actions.SUBSCRIPTION_SWITCH_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Cancel subscription
export function* deleteRequest() {
    yield takeEvery(actions.SUBSCRIPTION_DELETE_REQUEST, function* (payload, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(deleto, '/subscription', null, context);

            yield put({
                type: actions.SUBSCRIPTION_DELETE_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.SUBSCRIPTION_DELETE_FAILURE,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}
export function* deleteSuccess() {
    yield takeEvery(actions.SUBSCRIPTION_DELETE_SUCCESS, function* () {
        yield message.success(t`Your subscription has been cancelled.`);
    });
}
export function* deleteFailure() {
    yield takeEvery(actions.SUBSCRIPTION_DELETE_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Resume
export function* resumeRequest() {
    yield takeEvery(actions.SUBSCRIPTION_RESUME_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(post, '/subscription/resume', request, context);

            // OK
            yield put({
                type: actions.SUBSCRIPTION_RESUME_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.SUBSCRIPTION_RESUME_FAILURE,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}
export function* resumeSuccess() {
    yield takeEvery(actions.SUBSCRIPTION_RESUME_SUCCESS, function* () {
        yield message.success(t`Your subscription has been reactivated.`);
    });
}
export function* resumeFailure() {
    yield takeEvery(actions.SUBSCRIPTION_RESUME_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Pay
export function* payRequest() {
    yield takeEvery(actions.SUBSCRIPTION_PAY_REQUEST, function* ({ request }) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(post, '/subscription/charge', request);

            // OK
            yield put({
                type: actions.SUBSCRIPTION_PAY_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.SUBSCRIPTION_PAY_FAILURE,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}
export function* paySuccess() {
    yield takeEvery(actions.SUBSCRIPTION_PAY_SUCCESS, function* () {
        yield message.success(t`Your subscription has been successfully paid.`);
    });
}
export function* payFailure() {
    yield takeEvery(actions.SUBSCRIPTION_PAY_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Update payment method
export function* updatePaymentMethodRequest() {
    yield takeEvery(actions.SUBSCRIPTION_PAYMENT_METHOD_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(puto, '/subscription/payment_method', request, context);

            // OK
            yield put({
                type: actions.SUBSCRIPTION_PAYMENT_METHOD_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.SUBSCRIPTION_PAYMENT_METHOD_FAILURE,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}
export function* updatePaymentMethodSuccess() {
    yield takeEvery(actions.SUBSCRIPTION_PAYMENT_METHOD_SUCCESS, function* () {
        yield message.success(t`Your payment method has been updated.`);
    });
}
export function* updatePaymentMethodFailure() {
    yield takeEvery(actions.SUBSCRIPTION_PAYMENT_METHOD_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Read client secret
export function* readClientSecretRequest() {
    yield takeEvery(actions.SUBSCRIPTION_READ_CLIENT_SECRET_REQUEST, function* (payload, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, '/public/subscription/client_secret', null, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.SUBSCRIPTION_READ_CLIENT_SECRET_SUCCESS,
                clientSecret: data.client_secret,
            });
        }
        catch (error) {
            yield put({
                type: actions.SUBSCRIPTION_READ_CLIENT_SECRET_FAILURE,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}
export function* readClientSecretSuccess() {
    yield takeEvery(actions.SUBSCRIPTION_READ_CLIENT_SECRET_SUCCESS, () => {});
}
export function* readClientSecretFailure() {
    yield takeEvery(actions.SUBSCRIPTION_READ_CLIENT_SECRET_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Read company client secret
export function* readCompanyClientSecretRequest() {
    yield takeEvery(actions.SUBSCRIPTION_READ_COMPANY_CLIENT_SECRET_REQUEST, function* (payload, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, '/subscription/client_secret', null, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.SUBSCRIPTION_READ_COMPANY_CLIENT_SECRET_SUCCESS,
                clientSecret: data.client_secret,
            });
        }
        catch (error) {
            yield put({
                type: actions.SUBSCRIPTION_READ_COMPANY_CLIENT_SECRET_FAILURE,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}
export function* readCompanyClientSecretSuccess() {
    yield takeEvery(actions.SUBSCRIPTION_READ_COMPANY_CLIENT_SECRET_SUCCESS, () => {});
}
export function* readCompanyClientSecretFailure() {
    yield takeEvery(actions.SUBSCRIPTION_READ_COMPANY_CLIENT_SECRET_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Read invoices
export function* readInvoicesRequest() {
    yield takeEvery(actions.SUBSCRIPTION_READ_INVOICES_REQUEST, function* ({ payload }, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, '/subscription/invoices', payload, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.SUBSCRIPTION_READ_INVOICES_SUCCESS,
                invoices: data.invoices,
                pagination: data.paging,
            });
        }
        catch (error) {
            yield put({
                type: actions.SUBSCRIPTION_READ_INVOICES_FAILURE,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}
export function* readInvoicesSuccess() {
    yield takeEvery(actions.SUBSCRIPTION_READ_INVOICES_SUCCESS, () => {});
}
export function* readInvoicesFailure() {
    yield takeEvery(actions.SUBSCRIPTION_READ_INVOICES_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Read events
export function* readEventsRequest() {
    yield takeEvery(actions.SUBSCRIPTION_READ_EVENTS_REQUEST, function* ({ payload }, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, '/subscription/events', payload, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.SUBSCRIPTION_READ_EVENTS_SUCCESS,
                events: data.events,
                pagination: data.paging,
            });
        }
        catch (error) {
            yield put({
                type: actions.SUBSCRIPTION_READ_EVENTS_FAILURE,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}
export function* readEventsSuccess() {
    yield takeEvery(actions.SUBSCRIPTION_READ_EVENTS_SUCCESS, () => {});
}
export function* readEventsFailure() {
    yield takeEvery(actions.SUBSCRIPTION_READ_EVENTS_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

// top up storage
export function* updateStorageAddOnRequest() {
    yield takeEvery(actions.SUBSCRIPTION_UPDATE_STORAGE_ADD_ON_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(post, '/subscription/storage', request, context);

            // OK
            yield put({
                type: actions.SUBSCRIPTION_UPDATE_STORAGE_ADD_ON_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.SUBSCRIPTION_UPDATE_STORAGE_ADD_ON_FAILURE,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}
export function* updateStorageAddOnSuccess() {
    yield takeEvery(actions.SUBSCRIPTION_UPDATE_STORAGE_ADD_ON_SUCCESS, function* () {
        yield message.success(t`Your storage usage has been updated.`);
    });
}
export function* updateStorageAddOnFailure() {
    yield takeEvery(actions.SUBSCRIPTION_UPDATE_STORAGE_ADD_ON_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

// update quota addons
export function* updateAddonPacksRequest() {
    yield takeEvery(actions.SUBSCRIPTION_UPDATE_ADDON_PACKS_REQUEST, function* ({ id, request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(puto, `/subscription/addon_packs/${id}`, request, context);

            // OK
            yield put({
                type: actions.SUBSCRIPTION_UPDATE_ADDON_PACKS_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.SUBSCRIPTION_UPDATE_ADDON_PACKS_FAILURE,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}
export function* updateAddonPacksSuccess() {
    yield takeEvery(actions.SUBSCRIPTION_UPDATE_ADDON_PACKS_SUCCESS, function* () {
        yield message.success(t`Your addon usage has been updated.`);
    });
}
export function* updateAddonPacksFailure() {
    yield takeEvery(actions.SUBSCRIPTION_UPDATE_ADDON_PACKS_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Read current company's accesses and form designs
export function* readSwitchPlanInfoRequest() {
    yield takeEvery(actions.SUBSCRIPTION_READ_SWITCH_PLAN_INFO_REQUEST, function* (payload, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, '/subscription/switch_plan_info', null, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.SUBSCRIPTION_READ_SWITCH_PLAN_INFO_SUCCESS,
                accesses: data.accesses, // excluding advisors
                // accesses: [
                //     {
                //         id: 1,
                //         first_name: "User 1",
                //         email: "user1@bukku.my",
                //         is_owner: false,
                //     },
                //     {
                //         id: 2,
                //         first_name: "Marcus",
                //         email: "marcus@bukku.my",
                //         is_owner: false,
                //     },
                //     {
                //         id: 3,
                //         first_name: "User 3",
                //         email: "user3@bukku.my",
                //         is_owner: false,
                //     },
                //     {
                //         id: 4,
                //         first_name: "User 4 is owner",
                //         email: "user4@bukku.my",
                //         is_owner: true,
                //     },
                // ],
                formDesigns: data.form_designs,
                // formDesigns: [
                //     {
                //         id: 1,
                //         name: "FD1",
                //     },
                //     {
                //         id: 12,
                //         name: "FD12",
                //     },
                //     {
                //         id: 13,
                //         name: "FD13",
                //     },
                //     {
                //         id: 14,
                //         name: "FD14",
                //     },
                //     {
                //         id: 15,
                //         name: "FD15",
                //     },
                // ],
            });
        }
        catch (error) {
            yield put({
                type: actions.SUBSCRIPTION_READ_SWITCH_PLAN_INFO_FAILURE,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}
export function* readSwitchPlanInfoSuccess() {
    yield takeEvery(actions.SUBSCRIPTION_READ_SWITCH_PLAN_INFO_SUCCESS, () => {});
}
export function* readSwitchPlanInfoFailure() {
    yield takeEvery(actions.SUBSCRIPTION_READ_SWITCH_PLAN_INFO_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Preview Invoice
export function* previewInvoiceRequest() {
    yield debounce(300, actions.SUBSCRIPTION_PREVIEW_INVOICE_REQUEST, function* (
        { request, previewType, metaData: dummyMetaData },
        // context
    ) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            console.log('aaa SUBSCRIPTION_PREVIEW_INVOICE_REQUEST', { request, previewType, dummyMetaData });

            // to handle dummy data logics
            const {
                isDummy,
            } = dummyMetaData || {};
            let responseNow, responseNext;

            let invoiceNow;
            let invoiceNext;

            if (!isDummy) {
                // Preprocess invoiceNow vs invoiceNext based on previewType:
                // - Switch Billing Cycle?
                // - Switch Plan (No Switch Cycle) Or Partner Client?
                //      - Upgrade?
                //      - Downgrade?
                // - Subscribe?
                //      - During Trial?
                //      - After Trial?
                request.previewType = previewType; // for debug purposes
                switch (previewType) {
                    case PreviewInvoiceType.SUBSCRIBE_DURING_TRIAL:
                        // requestParam = request;
                        // requestParam.preview_mode = 'subscribe';
                        // requestParam.proration_behavior = 'none';
                        // requestParam.proration_at = request.proration_at;
                        // requestParam.proration_behavior = 'create_prorations';
                        // Disabled by Jye
                        // responseNext = yield call(post, '/subscription/preview_invoice', request, context);
                        break;
                    case PreviewInvoiceType.SWITCH_DURING_ACTIVE_TRIAL:
                        // requestParam = request;
                        // requestParam.preview_mode = 'subscribe';
                        // requestParam.created_at = request.created_at;
                        // responseNext = yield call(post, '/subscription/preview_invoice', request, context);
                        break;
                    case PreviewInvoiceType.SUBSCRIBE_AFTER_TRIAL:
                        // requestParam = request;
                        // requestParam.preview_mode = 'subscribe';
                        // requestParam.proration_behavior = 'none';
                        // requestParam.proration_at = null;
                        // Disabled by Jye
                        // responseNow = yield call(post, '/subscription/preview_invoice', request, context);
                        break;
                    case PreviewInvoiceType.SWITCH_SAME_CYCLE_UPGRADE:
                    case PreviewInvoiceType.SWITCH_SAME_CYCLE_SAME:
                    case PreviewInvoiceType.SWITCH_DIFF_BILLING_CYCLE:
                    case PreviewInvoiceType.SWITCH_DIFF_BILLING_CYCLE_TO_FREE_PLAN: { // added by marcus for ENG-3635
                        // const requestParamNow = {
                        // //     ...request,
                        // //     preview_mode: 'switch',
                        // //     proration_behavior: 'always_invoice',
                        // //     proration_at: dayjs().format('YYYY-MM-DD HH:mm'),
                        // // };
                        // const requestParamNext = {
                        //     ...request,
                        //     preview_mode: 'switch',
                        //     proration_behavior: 'create_prorations',
                        // };
                        // // requestParam.preview_mode = 'switch';
                        // // responseNow = yield call(post, '/subscription/preview_invoice', requestParamNow, context);
                        // Disabled by Jye
                        // responseNext = yield call(post, '/subscription/preview_invoice', request, context);
                        break;
                    }
                    case PreviewInvoiceType.SWITCH_SAME_CYCLE_DOWNGRADE:
                        // requestParam = request;
                        // requestParam.preview_mode = 'switch';
                        // requestParam.proration_behavior = 'none';
                        // requestParam.proration_at = null;
                        // Disabled by Jye
                        // responseNext = yield call(post, '/subscription/preview_invoice', request, context);
                        break;
                    default:
                        throw new Error(`Unknown previewType: ${previewType}`);
                }

                invoiceNow = yield responseNow?.data.invoice_preview || undefined;
                invoiceNext = yield responseNext?.data.invoice_preview || undefined;
            }
            // dummy data
            else {
                ({ invoiceNow, invoiceNext } = getDummyPreviewInvoice({
                    request, previewType, dummyMetaData,
                }));
            }

            // console.log('aaa SUBSCRIPTION_PREVIEW_INVOICE_REQUEST', { invoiceNow, invoiceNext });
            /** As Stripe cannot handle more than 1 upcoming invoice in future, processing is needed when switching billing cycle */
            if (invoiceNext)
                invoiceNext = postprocessInvoicePreview(previewType, invoiceNext);
            // console.log('aaa SUBSCRIPTION_PREVIEW_INVOICE_REQUEST 2', { invoiceNow, invoiceNext });

            // OK
            yield put({
                type: actions.SUBSCRIPTION_PREVIEW_INVOICE_SUCCESS,
                invoicePreview: {
                    today: invoiceNow,
                    next: invoiceNext,
                },
            });
            console.log('aaa SUBSCRIPTION_PREVIEW_INVOICE_SUCCESS', { invoiceNow, invoiceNext});
        }
        catch (error) {
            yield put({
                type: actions.SUBSCRIPTION_PREVIEW_INVOICE_FAILURE,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}

export function* previewInvoiceSuccess() {
    yield takeEvery(actions.SUBSCRIPTION_PREVIEW_INVOICE_SUCCESS, () => {});
}
export function* previewInvoiceFailure() {
    yield takeEvery(actions.SUBSCRIPTION_PREVIEW_INVOICE_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Read billing profile
export function* readBillingProfileRequest() {
    yield takeEvery(actions.SUBSCRIPTION_READ_BILLING_PROFILE_REQUEST, function* (payload, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, '/subscription/billing_profile', null, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.SUBSCRIPTION_READ_BILLING_PROFILE_SUCCESS,
                billingProfile: data.data,
            });
        }
        catch (error) {
            // 404 means the company hasn't filled in a billing profile yet — not an error
            if (error.statusCode === 404) {
                yield put({
                    type: actions.SUBSCRIPTION_READ_BILLING_PROFILE_SUCCESS,
                    billingProfile: null,
                });
            }
            else {
                yield put({
                    type: actions.SUBSCRIPTION_READ_BILLING_PROFILE_FAILURE,
                    error,
                });
            }
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}
export function* readBillingProfileSuccess() {
    yield takeEvery(actions.SUBSCRIPTION_READ_BILLING_PROFILE_SUCCESS, () => {});
}
export function* readBillingProfileFailure() {
    yield takeEvery(actions.SUBSCRIPTION_READ_BILLING_PROFILE_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Update billing profile
export function* updateBillingProfileRequest() {
    yield takeEvery(actions.SUBSCRIPTION_UPDATE_BILLING_PROFILE_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            const response = yield call(puto, '/subscription/billing_profile', request, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.SUBSCRIPTION_UPDATE_BILLING_PROFILE_SUCCESS,
                billingProfile: data.data,
            });
        }
        catch (error) {
            yield put({
                type: actions.SUBSCRIPTION_UPDATE_BILLING_PROFILE_FAILURE,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}
export function* updateBillingProfileSuccess() {
    yield takeEvery(actions.SUBSCRIPTION_UPDATE_BILLING_PROFILE_SUCCESS, function* () {
        yield message.success(t`Your billing information has been updated.`);
    });
}
export function* updateBillingProfileFailure() {
    yield takeEvery(actions.SUBSCRIPTION_UPDATE_BILLING_PROFILE_FAILURE, function* ({ error }) {
        yield message.error(error.message);
    });
}

export default function* rootSaga() {
    yield all([
        fork(readRequest),
        fork(readSuccess),
        fork(readFailure),
        fork(createRequest),
        fork(createSuccess),
        fork(createFailure),
        fork(switchRequest),
        fork(switchSuccess),
        fork(switchFailure),
        fork(deleteRequest),
        fork(deleteSuccess),
        fork(deleteFailure),
        fork(resumeRequest),
        fork(resumeSuccess),
        fork(resumeFailure),
        fork(payRequest),
        fork(paySuccess),
        fork(payFailure),
        fork(updatePaymentMethodRequest),
        fork(updatePaymentMethodSuccess),
        fork(updatePaymentMethodFailure),
        fork(readClientSecretRequest),
        fork(readClientSecretSuccess),
        fork(readClientSecretFailure),
        fork(readCompanyClientSecretRequest),
        fork(readCompanyClientSecretSuccess),
        fork(readCompanyClientSecretFailure),
        fork(readInvoicesRequest),
        fork(readInvoicesSuccess),
        fork(readInvoicesFailure),
        fork(readEventsRequest),
        fork(readEventsSuccess),
        fork(readEventsFailure),
        fork(updateStorageAddOnRequest),
        fork(updateStorageAddOnSuccess),
        fork(updateStorageAddOnFailure),
        fork(updateAddonPacksRequest),
        fork(updateAddonPacksSuccess),
        fork(updateAddonPacksFailure),
        fork(readSwitchPlanInfoRequest),
        fork(readSwitchPlanInfoSuccess),
        fork(readSwitchPlanInfoFailure),
        fork(previewInvoiceRequest),
        fork(previewInvoiceSuccess),
        fork(previewInvoiceFailure),
        fork(readBillingProfileRequest),
        fork(readBillingProfileSuccess),
        fork(readBillingProfileFailure),
        fork(updateBillingProfileRequest),
        fork(updateBillingProfileSuccess),
        fork(updateBillingProfileFailure),
    ]);
}
